import { computed, readonly, ref } from 'vue';

export const WIRING_STATUS = {
  TO_CHANGE: 'TO CHANGE',
  CHANGED: 'CHANGED',
};

function wiring(prodWiringFields, devWiringFields) {
  const wiringDraftProduction = ref({});
  const wiringDraftDevelopment = ref({});

  const wiringDraftTouchedProduction = ref({});
  const wiringDraftTouchedDevelopment = ref({});

  function _wiringFieldDefault(field) {
    return field?.type === 'checkbox' ? (field?.default ?? false) : (field?.default ?? '');
  }

  function _wiringFieldNormalize(field, v) {
    return field?.type === 'checkbox' ? Boolean(v) : String(v ?? '');
  }

  function _wiringFieldToUi(field, v) {
    if (field?.type === 'checkbox') return v ? 'true' : 'false';
    if (field?.type === 'select') {
      const raw = String(v ?? '');
      const hit = (Array.isArray(field?.options) ? field.options : []).find((o) => String(o?.id ?? '') === raw);
      return hit?.label ? String(hit.label) : raw;
    }
    return String(v ?? '');
  }

  function _buildWiringRows(scope, fields, draft, touched) {
    return Object.entries(fields || {}).map(([key, field]) => {
      const draftVal = draft?.[key];
      const backendVal = field?.value ?? _wiringFieldDefault(field);
      const effectiveVal = draftVal !== undefined ? draftVal : backendVal;

      const backendChangedFlag =
        _wiringFieldNormalize(field, backendVal) !== _wiringFieldNormalize(field, _wiringFieldDefault(field));
      const uiOverrideFlag =
        Boolean(touched?.[key]) && _wiringFieldNormalize(field, draftVal) !== _wiringFieldNormalize(field, backendVal);

      return {
        key: `${scope}.${key}`,
        label: field?.label || key,
        value: _wiringFieldToUi(field, effectiveVal),
        description: field?.description || '',
        backendChanged: backendChangedFlag,
        uiOverride: uiOverrideFlag,
        statusLabel: uiOverrideFlag ? WIRING_STATUS.TO_CHANGE : backendChangedFlag ? WIRING_STATUS.CHANGED : '',
        changed: backendChangedFlag || uiOverrideFlag,
      };
    });
  }

  function syncWiringDraftFromRemote() {
    const prod = prodWiringFields.value || {};
    const dev = devWiringFields.value || {};

    const nextProd = { ...(wiringDraftProduction.value || {}) };
    const nextDev = { ...(wiringDraftDevelopment.value || {}) };

    const touchedProd = wiringDraftTouchedProduction.value || {};
    const touchedDev = wiringDraftTouchedDevelopment.value || {};

    Object.entries(prod).forEach(([key, field]) => {
      if (touchedProd[key]) return;

      const raw = field?.value ?? field?.default ?? '';
      nextProd[key] = field?.type === 'checkbox' ? Boolean(raw) : raw;
    });

    Object.entries(dev).forEach(([key, field]) => {
      if (touchedDev[key]) return;

      const raw = field?.value ?? field?.default ?? '';
      nextDev[key] = field?.type === 'checkbox' ? Boolean(raw) : raw;
    });

    wiringDraftProduction.value = nextProd;
    wiringDraftDevelopment.value = nextDev;
  }

  function clearWiringDraft() {
    wiringDraftProduction.value = {};
    wiringDraftDevelopment.value = {};
    wiringDraftTouchedProduction.value = {};
    wiringDraftTouchedDevelopment.value = {};
  }

  function setWiringDraftValue(scope, key, value) {
    if (scope === 'production') {
      wiringDraftProduction.value = { ...(wiringDraftProduction.value || {}), [key]: value };
      wiringDraftTouchedProduction.value = { ...(wiringDraftTouchedProduction.value || {}), [key]: true };
      return;
    }

    if (scope === 'development') {
      wiringDraftDevelopment.value = { ...(wiringDraftDevelopment.value || {}), [key]: value };
      wiringDraftTouchedDevelopment.value = { ...(wiringDraftTouchedDevelopment.value || {}), [key]: true };
      return;
    }

    throw new Error(`wiring: Unhandled wiring scope: ${scope}`);
  }

  function resetWiringDraftToDefaults(scope = 'production') {
    const group = scope === 'production' ? prodWiringFields.value : devWiringFields.value;
    const next = {};

    Object.entries(group || {}).forEach(([key, field]) => {
      const raw = field?.default ?? '';
      next[key] = field?.type === 'checkbox' ? Boolean(raw) : raw;
    });

    const touched = Object.fromEntries(Object.keys(next).map((k) => [k, true]));

    if (scope === 'production') {
      wiringDraftProduction.value = next;
      wiringDraftTouchedProduction.value = touched;
      return;
    }

    if (scope === 'development') {
      wiringDraftDevelopment.value = next;
      wiringDraftTouchedDevelopment.value = touched;
      return;
    }

    throw new Error(`wiring: Unhandled wiring scope: ${scope}`);
  }

  const wiringValues = computed(() => {
    const merged = {
      ...(wiringDraftProduction.value || {}),
      ...(wiringDraftDevelopment.value || {}),
    };

    Object.entries(devWiringFields.value || {}).forEach(([key, field]) => {
      if (field?.type === 'checkbox') merged[key] = Boolean(merged[key]);
    });

    return merged;
  });

  const wiringProductionChanged = computed(() => {
    const fields = prodWiringFields.value || {};
    const draft = wiringDraftProduction.value || {};

    return Object.entries(fields).some(([key, field]) => {
      const draftValue = draft[key];
      const remoteValue = field?.value ?? field?.default ?? '';

      if (field?.type === 'checkbox') return Boolean(draftValue) !== Boolean(remoteValue);

      return String(draftValue ?? '') !== String(remoteValue ?? '');
    });
  });

  const wiringDisplayRows = computed(() => {
    const prod = _buildWiringRows(
      'production',
      prodWiringFields.value,
      wiringDraftProduction.value,
      wiringDraftTouchedProduction.value,
    );
    const dev = _buildWiringRows(
      'development',
      devWiringFields.value,
      wiringDraftDevelopment.value,
      wiringDraftTouchedDevelopment.value,
    );
    return [...prod, ...dev].filter((r) => r.changed);
  });

  const wiringDisplayMode = computed(() => (wiringDisplayRows.value.length ? 'custom' : 'default'));
  const wiringHasUiOverrides = computed(() => wiringDisplayRows.value.some((r) => r.uiOverride));
  const wiringHasChanges = computed(() => wiringDisplayRows.value.length > 0);

  return {
    wiringDraftProduction: readonly(wiringDraftProduction),
    wiringDraftDevelopment: readonly(wiringDraftDevelopment),
    wiringDraftTouchedProduction: readonly(wiringDraftTouchedProduction),
    wiringDraftTouchedDevelopment: readonly(wiringDraftTouchedDevelopment),
    wiringValues,
    wiringProductionChanged,
    wiringDisplayRows: readonly(wiringDisplayRows),
    wiringDisplayMode,
    wiringHasUiOverrides,
    wiringHasChanges,
    syncWiringDraftFromRemote,
    clearWiringDraft,
    setWiringDraftValue,
    resetWiringDraftToDefaults,
  };
}

export default wiring;
